import { isNil } from '#layers/base/utils/fn';

type SortableKeys<Item> = keyof Pick<Item, {
	[key in keyof Item]: Item[key] extends number | string | null | undefined ? key : never;
}[keyof Item]>;

export function compareSortable<
	Item,
	SortField extends keyof Item = 'sort' extends keyof Item ? 'sort' : never,
>(
	a: Item,
	b: SortField extends SortableKeys<Item> ? Item : never,
	field: SortField = 'sort' as SortField,
): number {
	const valueA = a[field] as number | null | undefined;
	const valueB = b[field] as number | null | undefined;
	if (valueA === valueB) return 0;
	if (isNil(valueA)) return 1;
	if (isNil(valueB)) return -1;
	return valueA > valueB ? 1 : -1;
}

function createSortableComparator<
	Item,
	SortField extends keyof Item = 'sort' extends keyof Item ? 'sort' : never,
>(
	field: SortField = 'sort' as SortField,
	descending: boolean = false,
) {
	return (a: Item, b: SortField extends SortableKeys<Item> ? Item : never) => compareSortable(a, b, field) * (descending ? -1 : 1);
}

export function toSortedArray<
	Item,
	SortField extends keyof Item = 'sort' extends keyof Item ? 'sort' : never,
>(
	array: Item[],
	field: SortField = 'sort' as SortField,
	descending: boolean = false,
): SortField extends SortableKeys<Item> ? Item[] : never {
	const comparator = createSortableComparator<Item, SortField>(field, descending) as (a: Item, b: Item) => number;
	return array.toSorted(comparator) as Item[] as any;
}
